// The specific reasons this module's backend gives, in the four languages.
//
// Two codes travel INSIDE an error and are not the code it is thrown with:
//
//   · `reasons` — what a generic refusal carries in `details.reason` or
//     `details.code` (`ConflictError(…, 'CONFLICT', { reason: 'ALREADY_CLOSED' })`).
//     errorMessage() says it from `errors.reasons.<REASON>` instead of the
//     generic «that record is no longer in the state you expected».
//   · `fields` — the code of each field of a ValidationError, said by
//     fieldErrors() from `errors.fields.<CODE>` instead of «not a valid value».
//
// Generic field codes (EMPTY, IN_THE_FUTURE, NOT_FOUND…) are the shell's and
// are not repeated here. platform/web/tests/errorReasons.test.js reads the
// backend and fails on a code without its four sentences, and on two
// modules saying one code differently (the shell merges them into one).

const errorReasons = {
  es: {
    fields: {
      ABOVE_MAX: 'Por encima del máximo permitido',
      BELOW_MIN: 'Por debajo del mínimo permitido',
      ALREADY_IN_STATUS: 'Ya está en ese estado',
      ALREADY_VOID: 'Ya estaba anulado',
      BAD_LEVEL: 'Ese nivel no puede colgar de ahí',
      CODE_REQUIRED: 'Falta el código',
      IMPLAUSIBLE_HIGH: 'Más alto de lo que puede marcar este contador',
      IMPLAUSIBLE_LOW: 'Más bajo de lo que puede marcar este contador',
      NAME_REQUIRED: 'Falta el nombre',
      NOTHING_TO_CLASSIFY: 'No hay nada que clasificar',
      NOT_AN_OPTION: 'No es una de las opciones',
      NO_FILE: 'Adjunta un fichero o un enlace',
      PARENT_NOT_FOUND: 'No se encuentra de dónde cuelga',
      REASON_REQUIRED: 'Di por qué',
      SUPERSEDES_NOT_FOUND: 'El documento que sustituye no es de esta máquina',
      TITLE_REQUIRED: 'Falta el título',
      UNKNOWN_STATUS: 'Ese estado no existe',
      WENT_BACK: 'Es menor que la última lectura: se guarda, pero el contador no se mueve',
    },
  },
  ca: {
    fields: {
      ABOVE_MAX: 'Per sobre del màxim permès',
      BELOW_MIN: 'Per sota del mínim permès',
      ALREADY_IN_STATUS: 'Ja és en aquest estat',
      ALREADY_VOID: 'Ja estava anul·lat',
      BAD_LEVEL: 'Aquest nivell no pot penjar d’aquí',
      CODE_REQUIRED: 'Falta el codi',
      IMPLAUSIBLE_HIGH: 'Més alt del que pot marcar aquest comptador',
      IMPLAUSIBLE_LOW: 'Més baix del que pot marcar aquest comptador',
      NAME_REQUIRED: 'Falta el nom',
      NOTHING_TO_CLASSIFY: 'No hi ha res per classificar',
      NOT_AN_OPTION: 'No és una de les opcions',
      NO_FILE: 'Adjunta un fitxer o un enllaç',
      PARENT_NOT_FOUND: 'No es troba d’on penja',
      REASON_REQUIRED: 'Digues per què',
      SUPERSEDES_NOT_FOUND: 'El document que substitueix no és d’aquesta màquina',
      TITLE_REQUIRED: 'Falta el títol',
      UNKNOWN_STATUS: 'Aquest estat no existeix',
      WENT_BACK: 'És menor que l’última lectura: es desa, però el comptador no es mou',
    },
  },
  en: {
    fields: {
      ABOVE_MAX: 'Above the maximum allowed',
      BELOW_MIN: 'Below the minimum allowed',
      ALREADY_IN_STATUS: 'It is already in that state',
      ALREADY_VOID: 'It was already void',
      BAD_LEVEL: 'That level cannot hang from there',
      CODE_REQUIRED: 'The code is missing',
      IMPLAUSIBLE_HIGH: 'Higher than this meter can read',
      IMPLAUSIBLE_LOW: 'Lower than this meter can read',
      NAME_REQUIRED: 'The name is missing',
      NOTHING_TO_CLASSIFY: 'There is nothing to classify',
      NOT_AN_OPTION: 'That is not one of the options',
      NO_FILE: 'Attach a file or a link',
      PARENT_NOT_FOUND: 'What it hangs from could not be found',
      REASON_REQUIRED: 'Say why',
      SUPERSEDES_NOT_FOUND: 'The document it replaces is not on this machine',
      TITLE_REQUIRED: 'The title is missing',
      UNKNOWN_STATUS: 'That status does not exist',
      WENT_BACK: 'Lower than the last reading: it is kept, but the counter does not move',
    },
  },
  fr: {
    fields: {
      ABOVE_MAX: 'Au-dessus du maximum autorisé',
      BELOW_MIN: 'En dessous du minimum autorisé',
      ALREADY_IN_STATUS: 'Il est déjà dans cet état',
      ALREADY_VOID: 'C’était déjà annulé',
      BAD_LEVEL: 'Ce niveau ne peut pas dépendre de celui-ci',
      CODE_REQUIRED: 'Le code manque',
      IMPLAUSIBLE_HIGH: 'Plus élevé que ce que ce compteur peut afficher',
      IMPLAUSIBLE_LOW: 'Plus bas que ce que ce compteur peut afficher',
      NAME_REQUIRED: 'Le nom manque',
      NOTHING_TO_CLASSIFY: 'Il n’y a rien à classer',
      NOT_AN_OPTION: 'Ce n’est pas l’une des options',
      NO_FILE: 'Joignez un fichier ou un lien',
      PARENT_NOT_FOUND: 'L’élément parent est introuvable',
      REASON_REQUIRED: 'Indiquez pourquoi',
      SUPERSEDES_NOT_FOUND: 'Le document remplacé n’est pas sur cette machine',
      TITLE_REQUIRED: 'Le titre manque',
      UNKNOWN_STATUS: 'Ce statut n’existe pas',
      WENT_BACK: 'Inférieur au dernier relevé : il est conservé, mais le compteur ne bouge pas',
    },
  },
}

/** The module's catalogue with these hung from `errors`, locale by locale. */
export function withErrorReasons(base) {
  const merged = {}
  for (const [locale, catalogue] of Object.entries(base)) {
    const own = errorReasons[locale] ?? {}
    merged[locale] = {
      ...catalogue,
      errors: {
        ...catalogue.errors,
        reasons: { ...catalogue.errors?.reasons, ...own.reasons },
        fields: { ...catalogue.errors?.fields, ...own.fields },
      },
    }
  }
  return merged
}

export default errorReasons
